// Sign-in. With VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY: Supabase email magic link, the access token is
// sent as Bearer. Otherwise: paste an API key (fsk_…), kept in localStorage.
import { AuthClient } from "@supabase/auth-js";
import { buildUrl, defaultBaseUrl } from "./api.ts";
import { SignupError, type SignupResult } from "./signup.ts";

const KEY_STORAGE = "filmsquare.apiKey";
const PICK_STORAGE = "filmsquare.picks";

export function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // private window or blocked storage: the page works without persistence
  }
}

export const loadApiKey = (): string | null => safeGet(KEY_STORAGE);
export const saveApiKey = (key: string | null): void => safeSet(KEY_STORAGE, key?.trim() || null);

export interface Picks {
  ws?: string;
  project?: string;
  episode?: string;
}

export function loadPicks(): Picks {
  try {
    const raw = safeGet(PICK_STORAGE);
    return raw ? (JSON.parse(raw) as Picks) : {};
  } catch {
    return {};
  }
}

export const savePicks = (p: Picks): void => safeSet(PICK_STORAGE, JSON.stringify(p));

export type AuthMode = "supabase" | "apikey";

export interface AuthEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
}

export const authMode = (env: AuthEnv = import.meta.env): AuthMode =>
  env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY ? "supabase" : "apikey";

type SignIn = { auth: InstanceType<typeof AuthClient> };

let supabase: SignIn | undefined;
/**
 * Only sign-in, not the whole Supabase client (database, storage, realtime): most of the download otherwise. The
 * options are the ones that client passes; the storage key above all, or everyone would be signed out.
 */
export function supabaseClient(env: AuthEnv = import.meta.env): SignIn {
  if (!supabase) {
    const base = new URL(env.VITE_SUPABASE_URL!.replace(/\/*$/, "/"));
    const key = env.VITE_SUPABASE_ANON_KEY!;
    supabase = {
      auth: new AuthClient({
        url: new URL("auth/v1", base).href,
        headers: { Authorization: `Bearer ${key}`, apikey: key },
        storageKey: `sb-${base.hostname.split(".")[0]}-auth-token`,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: "implicit",
      }),
    };
  }
  return supabase;
}

/** The bearer token for the current session, or null when signed out. */
export async function currentToken(mode: AuthMode): Promise<string | null> {
  if (mode === "apikey") return loadApiKey();
  const { data } = await supabaseClient().auth.getSession();
  return data.session?.access_token ?? null;
}

export async function sendMagicLink(email: string): Promise<void> {
  const { error } = await supabaseClient().auth.signInWithOtp({
    email,
    // sign-up is by access code or invitation (POST /v1/signup), never by asking for a link
    options: { emailRedirectTo: window.location.origin, shouldCreateUser: false },
  });
  if (error) throw error;
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await supabaseClient().auth.signInWithPassword({ email, password });
  if (error) throw error;
}

/**
 * Make the account for a new address (no sign-in needed): with an access code or an invitation token (optional
 * when sign-up is open), a Turnstile token when the page showed the challenge, and the password it is made with.
 * An address that already has an account answers "exists" and keeps its password. `acceptTerms` is the terms version
 * the person agreed to (400 `terms_required` without it).
 */
export async function signUp(
  email: string,
  code?: string,
  inviteToken?: string,
  password?: string,
  o: { baseUrl?: string; fetch?: typeof fetch; turnstileToken?: string; acceptTerms?: string } = {},
): Promise<SignupResult> {
  const doFetch = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const res = await doFetch(buildUrl(o.baseUrl ?? defaultBaseUrl(), "/signup"), {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({
      email,
      ...(code ? { code } : {}),
      ...(inviteToken ? { inviteToken } : {}),
      ...(password ? { password } : {}),
      ...(o.turnstileToken ? { turnstileToken: o.turnstileToken } : {}),
      ...(o.acceptTerms ? { acceptTerms: o.acceptTerms } : {}),
    }),
  });
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | undefined)
      ?.error;
    throw new SignupError(
      res.status,
      err?.code ?? "http_error",
      err?.message ?? (text.slice(0, 200) || res.statusText || `HTTP ${res.status}`),
      err?.details,
    );
  }
  return json as SignupResult;
}

/** What one credit costs in euros, from the server's price table; the launch price when it cannot be read. */
export async function fetchCreditEur(o: { baseUrl?: string; fetch?: typeof fetch } = {}): Promise<number> {
  const doFetch = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  try {
    const res = await doFetch(buildUrl(o.baseUrl ?? defaultBaseUrl(), "/pricing"), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return 0.05;
    const v = ((await res.json()) as { credit_eur?: unknown }).credit_eur;
    return typeof v === "number" && v > 0 ? v : 0.05;
  } catch {
    return 0.05;
  }
}

export async function signOut(mode: AuthMode): Promise<void> {
  if (mode === "apikey") saveApiKey(null);
  else await supabaseClient().auth.signOut();
}

export function isApiKey(s: string): boolean {
  return /^fsk_\S+$/.test(s.trim());
}
